/**
 * Padding Handles System
 *
 * Visual system for adjusting padding/margins on template elements via drag handles.
 * Initially designed for email widgets but generic enough for other elements.
 */

// eslint-disable-next-line import/no-cycle
import { TemplateEditorState as state, updateSectionsInState, setActiveSection } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument, getSectionParent, isEmailTemplate } from './helpers';
// eslint-disable-next-line import/no-cycle
import { addOrUpdateDynamicStyle, removeDynamicStyle } from './parser';
// eslint-disable-next-line import/no-cycle
import { createHistory } from '@/composables/template-editor/History';
import { HistoryType } from '@/types';
import { store } from '@/store';

const isMobileEditMode = (): boolean => store.getters['liveEditor/getMobileEditMode'] ?? false;

// Constants
export const PADDING_HANDLE_CLASS = 'spm-padding-handle';
export const PADDING_HANDLE_CONTAINER_CLASS = 'spm-padding-handles-container';
export const PADDING_INDICATOR_CLASS = 'spm-padding-indicator';
export const PADDING_OVERLAY_CLASS = 'spm-padding-overlay';

type PaddingDirection = 'top' | 'right' | 'bottom' | 'left';

interface PaddingHandleConfig {
  /** CSS selector for target elements */
  selector: string;
  /** Whether to show pixel indicators while dragging */
  showIndicator: boolean;
  /** Minimum padding value in pixels */
  minPadding: number;
  /** Maximum padding value in pixels */
  maxPadding: number;
  /** Step value for padding changes */
  step: number;
  /** Callback when padding changes */
  onPaddingChange?: (element: HTMLElement, direction: PaddingDirection, value: number) => void;
}

interface DragState {
  isDragging: boolean;
  direction: PaddingDirection | null;
  startY: number;
  startX: number;
  startPadding: number;
  targetElement: HTMLElement | null;
  handleElement: HTMLElement | null;
  indicator: HTMLElement | null;
}

// Default configuration for email widgets
export const EMAIL_WIDGET_PADDING_CONFIG: PaddingHandleConfig = {
  selector: '#spm_body table.spm_draggable_widget > tbody > tr > th',
  showIndicator: true,
  minPadding: 0,
  maxPadding: 100,
  step: 1,
};

// Configuration for email rows/lines
export const EMAIL_ROW_PADDING_CONFIG: PaddingHandleConfig = {
  selector: '#spm_body table.spm_draggable_row > tbody > tr > td',
  showIndicator: true,
  minPadding: 0,
  maxPadding: 100,
  step: 1,
};

// Configuration for display widgets (embed, popup, teaser v4) - simpler DIV-based structure
export const DISPLAY_WIDGET_PADDING_CONFIG: PaddingHandleConfig = {
  selector: '#spm_body .spm_draggable_widget',
  showIndicator: true,
  minPadding: 0,
  maxPadding: 100,
  step: 1,
};

// CSS class for row handles (different color)
export const PADDING_HANDLE_ROW_CLASS = 'spm-padding-handle-row';
export const PADDING_HANDLE_ROW_CONTAINER_CLASS = 'spm-padding-handles-container-row';
export const PADDING_OVERLAY_ROW_CLASS = 'spm-padding-overlay-row';

// Global drag state
let dragState: DragState = {
  isDragging: false,
  direction: null,
  startY: 0,
  startX: 0,
  startPadding: 0,
  targetElement: null,
  handleElement: null,
  indicator: null,
};

// Flag to prevent widget click events during padding drag
let isDraggingPaddingFlag = false;

// Active drag listener references (stored for proper removeEventListener)
let activeMoveListenerIframe: ((e: Event) => void) | null = null;
let activeUpListenerIframe: ((e: Event) => void) | null = null;
let activeMoveListenerDoc: ((e: Event) => void) | null = null;
let activeUpListenerDoc: ((e: Event) => void) | null = null;

/**
 * Cleanup drag listeners from both iframe and parent document
 */
const cleanupDragListeners = (): void => {
  const template = getTemplateIframeDocument();
  if (template) {
    if (activeMoveListenerIframe) template.removeEventListener('mousemove', activeMoveListenerIframe);
    if (activeUpListenerIframe) template.removeEventListener('mouseup', activeUpListenerIframe);
  }
  if (activeMoveListenerDoc) document.removeEventListener('mousemove', activeMoveListenerDoc);
  if (activeUpListenerDoc) document.removeEventListener('mouseup', activeUpListenerDoc);
  activeMoveListenerIframe = null;
  activeUpListenerIframe = null;
  activeMoveListenerDoc = null;
  activeUpListenerDoc = null;
};

// Export getter for the flag (read-only access from outside)
export const isDraggingPadding = (): boolean => isDraggingPaddingFlag;

// Track active handles containers (use Map instead of WeakMap to allow clear())
const activeHandlesMap = new Map<HTMLElement, HTMLElement>();
// Track active row handles containers separately
const activeRowHandlesMap = new Map<HTMLElement, HTMLElement>();

/**
 * Get computed padding value for an element in a specific direction
 */
const getPaddingValue = (element: HTMLElement, direction: PaddingDirection): number => {
  const template = getTemplateIframeDocument();
  const computedStyle = template.defaultView?.getComputedStyle(element);
  if (!computedStyle) return 0;

  const paddingProperty = `padding-${direction}` as keyof CSSStyleDeclaration;
  const value = computedStyle.getPropertyValue(`padding-${direction}`);
  return parseInt(value, 10) || 0;
};

/**
 * Create the CSS styles for padding handles (injected into iframe)
 */
export const injectPaddingHandlesStyles = (): void => {
  const template = getTemplateIframeDocument();
  if (!template) {
    return;
  }

  const existingStyles = template.getElementById('spm-padding-handles-styles');
  if (existingStyles) {
    return;
  }

  const styleElement = template.createElement('style');
  styleElement.id = 'spm-padding-handles-styles';
  styleElement.textContent = `
    /* Padding Handles Container */
    .${PADDING_HANDLE_CONTAINER_CLASS} {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      pointer-events: none !important;
      z-index: 100000002 !important;
      box-sizing: border-box !important;
    }

    /* Common handle styles */
    .${PADDING_HANDLE_CLASS} {
      position: absolute !important;
      background: #6366F1 !important;
      opacity: 0.6 !important;
      transition: opacity 0.15s ease;
      pointer-events: auto !important;
      z-index: 100000 !important;
    }

    .${PADDING_HANDLE_CLASS}:hover,
    .${PADDING_HANDLE_CLASS}.active {
      opacity: 1 !important;
    }

    /* Handle positions - positioned dynamically inside padding */
    .${PADDING_HANDLE_CLASS}[data-direction="top"] {
      left: 50% !important;
      width: 50px !important;
      height: 6px !important;
      border-radius: 4px !important;
      cursor: ns-resize !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="bottom"] {
      left: 50% !important;
      width: 50px !important;
      height: 6px !important;
      border-radius: 4px !important;
      cursor: ns-resize !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="left"] {
      top: 50% !important;
      width: 6px !important;
      height: 50px !important;
      border-radius: 4px !important;
      cursor: ew-resize !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="right"] {
      top: 50% !important;
      width: 6px !important;
      height: 50px !important;
      border-radius: 4px !important;
      cursor: ew-resize !important;
    }

    /* Padding indicator */
    .${PADDING_INDICATOR_CLASS} {
      position: fixed;
      background: rgba(99, 102, 241, 0.95);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-weight: 500;
      pointer-events: none;
      z-index: 100001;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    /* Custom tooltip for handles */
    .spm-padding-tooltip {
      position: absolute !important;
      background: rgba(30, 30, 30, 0.95) !important;
      color: #fff !important;
      padding: 8px 12px !important;
      border-radius: 6px !important;
      font-size: 11px !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
      line-height: 1.5 !important;
      white-space: nowrap !important;
      pointer-events: none !important;
      opacity: 0 !important;
      visibility: hidden !important;
      transition: opacity 0.2s ease, visibility 0.2s ease !important;
      z-index: 100000003 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    }

    .spm-padding-tooltip::before {
      content: '' !important;
      position: absolute !important;
      border: 5px solid transparent !important;
    }

    .${PADDING_HANDLE_CLASS}:hover .spm-padding-tooltip {
      opacity: 1 !important;
      visibility: visible !important;
    }

    .${PADDING_HANDLE_CLASS}.active .spm-padding-tooltip {
      opacity: 0 !important;
      visibility: hidden !important;
    }

    /* Tooltip positions based on handle direction */
    .${PADDING_HANDLE_CLASS}[data-direction="top"] .spm-padding-tooltip {
      bottom: 100% !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      margin-bottom: 8px !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="bottom"] .spm-padding-tooltip {
      top: 100% !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      margin-top: 8px !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="left"] .spm-padding-tooltip {
      right: 100% !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-right: 8px !important;
    }

    .${PADDING_HANDLE_CLASS}[data-direction="right"] .spm-padding-tooltip {
      left: 100% !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin-left: 8px !important;
    }

    .spm-padding-tooltip-title {
      font-weight: 600 !important;
      margin-bottom: 4px !important;
      color: #a78bfa !important;
    }

    .spm-padding-tooltip-item {
      color: #e5e5e5 !important;
    }

    /* Padding overlay visualization - visible on hover */
    .${PADDING_OVERLAY_CLASS} {
      position: absolute !important;
      background: rgba(99, 102, 241, 0.15) !important;
      pointer-events: none !important;
      z-index: 99998 !important;
    }

    .${PADDING_OVERLAY_CLASS}[data-direction="top"] {
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
    }

    .${PADDING_OVERLAY_CLASS}[data-direction="bottom"] {
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
    }

    .${PADDING_OVERLAY_CLASS}[data-direction="left"] {
      left: 0 !important;
      /* top and bottom set dynamically to avoid overlap */
    }

    .${PADDING_OVERLAY_CLASS}[data-direction="right"] {
      right: 0 !important;
      /* top and bottom set dynamically to avoid overlap */
    }

    /* ============================================ */
    /* ROW/LINE HANDLES - Orange color scheme      */
    /* ============================================ */
    
    .${PADDING_HANDLE_ROW_CONTAINER_CLASS} {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      pointer-events: none !important;
      z-index: 99998 !important;
      box-sizing: border-box !important;
    }

    .${PADDING_HANDLE_ROW_CLASS} {
      position: absolute !important;
      background: #F97316 !important;
      opacity: 0.6 !important;
      transition: opacity 0.15s ease;
      pointer-events: auto !important;
      z-index: 99999 !important;
    }

    .${PADDING_HANDLE_ROW_CLASS}:hover,
    .${PADDING_HANDLE_ROW_CLASS}.active {
      opacity: 1 !important;
    }

    .${PADDING_HANDLE_ROW_CLASS}[data-direction="top"] {
      left: 50% !important;
      width: 50px !important;
      height: 6px !important;
      border-radius: 4px !important;
      cursor: ns-resize !important;
    }

    .${PADDING_HANDLE_ROW_CLASS}[data-direction="bottom"] {
      left: 50% !important;
      width: 50px !important;
      height: 6px !important;
      border-radius: 4px !important;
      cursor: ns-resize !important;
    }

    .${PADDING_HANDLE_ROW_CLASS}[data-direction="left"] {
      top: 50% !important;
      width: 6px !important;
      height: 50px !important;
      border-radius: 4px !important;
      cursor: ew-resize !important;
    }

    .${PADDING_HANDLE_ROW_CLASS}[data-direction="right"] {
      top: 50% !important;
      width: 6px !important;
      height: 50px !important;
      border-radius: 4px !important;
      cursor: ew-resize !important;
    }

    .${PADDING_OVERLAY_ROW_CLASS} {
      position: absolute !important;
      background: rgba(249, 115, 22, 0.15) !important;
      pointer-events: none !important;
      z-index: 99997 !important;
    }

    .${PADDING_OVERLAY_ROW_CLASS}[data-direction="top"] {
      left: 0 !important;
      right: 0 !important;
      /* top position set dynamically - positioned above the widget */
    }

    .${PADDING_OVERLAY_ROW_CLASS}[data-direction="bottom"] {
      left: 0 !important;
      right: 0 !important;
      /* bottom position set dynamically - positioned below the widget */
    }

    .${PADDING_OVERLAY_ROW_CLASS}[data-direction="left"] {
      left: 0 !important;
    }

    .${PADDING_OVERLAY_ROW_CLASS}[data-direction="right"] {
      right: 0 !important;
    }
  `;

  template.head.appendChild(styleElement);
};

/**
 * Create a padding handle element
 */
const createHandle = (direction: PaddingDirection): HTMLElement => {
  const template = getTemplateIframeDocument();
  const handle = template.createElement('div');
  handle.className = PADDING_HANDLE_CLASS;
  handle.setAttribute('data-direction', direction);
  
  // Custom tooltip element
  const tooltip = template.createElement('div');
  tooltip.className = 'spm-padding-tooltip';
  const mobileLabel = isMobileEditMode()
    ? '<div class="spm-padding-tooltip-title">📱 Mode mobile</div>'
    : '';
  tooltip.innerHTML = `
    ${mobileLabel}
    <div class="spm-padding-tooltip-item">Glisser : ajuster</div>
    <div class="spm-padding-tooltip-item">Ctrl + glisser : 4 côtés</div>
    <div class="spm-padding-tooltip-item">Double-clic : reset</div>
  `;
  handle.appendChild(tooltip);
  
  return handle;
};

/**
 * Create padding indicator element
 */
const createIndicator = (): HTMLElement => {
  const template = getTemplateIframeDocument();
  const indicator = template.createElement('div');
  indicator.className = PADDING_INDICATOR_CLASS;
  indicator.style.display = 'none';
  template.body.appendChild(indicator);
  return indicator;
};

/**
 * Create padding overlay visualization
 * For left/right overlays, pass topPadding and bottomPadding to avoid overlap
 */
const createOverlay = (
  direction: PaddingDirection,
  size: number,
  topPadding: number = 0,
  bottomPadding: number = 0,
): HTMLElement => {
  const template = getTemplateIframeDocument();
  const overlay = template.createElement('div');
  overlay.className = PADDING_OVERLAY_CLASS;
  overlay.setAttribute('data-direction', direction);

  if (direction === 'top' || direction === 'bottom') {
    overlay.style.height = `${size}px`;
  } else {
    // For left/right, set width and offset by top/bottom padding to avoid overlap
    overlay.style.width = `${size}px`;
    overlay.style.top = `${topPadding}px`;
    overlay.style.bottom = `${bottomPadding}px`;
  }

  return overlay;
};

/**
 * Update the position of the padding indicator
 */
const updateIndicatorPosition = (event: MouseEvent, value: number): void => {
  if (!dragState.indicator) return;

  dragState.indicator.style.display = 'block';
  dragState.indicator.textContent = `${value} pixels`;

  // Position near cursor
  const offset = 15;
  dragState.indicator.style.left = `${event.clientX + offset}px`;
  dragState.indicator.style.top = `${event.clientY + offset}px`;
};

/**
 * Hide the padding indicator
 */
const hideIndicator = (): void => {
  if (dragState.indicator) {
    dragState.indicator.style.display = 'none';
  }
};

/**
 * Build the CSS selector for an element
 */
const buildElementSelector = (element: HTMLElement): string => {
  // Try to find the widget parent to build a proper selector
  const widget = element.closest('[id^="spm_widget_"]');
  if (widget && widget.id) {
    // Email templates use table structure: > tbody > tr > th/td
    // Display templates: widget IS the target element directly
    if (isEmailTemplate(state.template?.type)) {
      const tagName = element.tagName.toLowerCase();
      return `body #spm_body #${widget.id} > tbody > tr > ${tagName}`;
    }
    // Display: widget itself is the target
    return `body #spm_body #${widget.id}`;
  }

  // Fallback: use element's own id
  if (element.id) {
    return `body #spm_body #${element.id}`;
  }

  return '';
};

/**
 * Apply padding change to element via CSS
 */
const applyPaddingChange = (
  element: HTMLElement,
  direction: PaddingDirection,
  value: number,
  config: PaddingHandleConfig,
): void => {
  const selector = buildElementSelector(element);
  if (!selector) return;

  // Clamp value
  const clampedValue = Math.max(config.minPadding, Math.min(config.maxPadding, value));

  // Apply via dynamic styles only (no inline styles)
  addOrUpdateDynamicStyle(selector, {
    [`padding-${direction}`]: `${clampedValue}px`,
  }, isMobileEditMode());

  // Callback
  if (config.onPaddingChange) {
    config.onPaddingChange(element, direction, clampedValue);
  }
};

/**
 * Update all overlays and handles when Ctrl+drag is used (all 4 sides)
 */
const updateAllOverlaysAndHandles = (
  element: HTMLElement,
  padding: number,
  config: PaddingHandleConfig,
): void => {
  const container = activeHandlesMap.get(element);
  if (!container) return;

  const allDirections: PaddingDirection[] = ['top', 'right', 'bottom', 'left'];
  
  allDirections.forEach((dir) => {
    // Update overlay
    const overlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="${dir}"]`) as HTMLElement;
    if (overlay) {
      if (dir === 'top' || dir === 'bottom') {
        overlay.style.height = `${padding}px`;
      } else {
        overlay.style.width = `${padding}px`;
      }
    }

    // Update left/right overlays positions
    const leftOverlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="left"]`) as HTMLElement;
    const rightOverlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="right"]`) as HTMLElement;
    if (leftOverlay) {
      leftOverlay.style.top = `${padding}px`;
      leftOverlay.style.bottom = `${padding}px`;
    }
    if (rightOverlay) {
      rightOverlay.style.top = `${padding}px`;
      rightOverlay.style.bottom = `${padding}px`;
    }

    // Update handle position
    const handle = container.querySelector(`.${PADDING_HANDLE_CLASS}[data-direction="${dir}"]`) as HTMLElement;
    if (handle) {
      positionHandleInsidePadding(handle, dir, padding);
    }
  });
};

/**
 * Handle double-click on a padding handle to reset all padding CSS
 */
const handleDoubleClick = (
  event: MouseEvent,
  targetElement: HTMLElement,
  config: PaddingHandleConfig,
): void => {
  event.preventDefault();
  event.stopPropagation();

  // Block widget click events during double-click
  isDraggingPaddingFlag = true;
  setTimeout(() => {
    isDraggingPaddingFlag = false;
  }, 150);

  const selector = buildElementSelector(targetElement);
  if (!selector) return;

  // Remove all padding styles from dynamic styles
  const paddingStyles = {
    'padding-top': '',
    'padding-right': '',
    'padding-bottom': '',
    'padding-left': '',
  };
  removeDynamicStyle(selector, paddingStyles, isMobileEditMode());

  // Remove inline styles
  targetElement.style.removeProperty('padding-top');
  targetElement.style.removeProperty('padding-right');
  targetElement.style.removeProperty('padding-bottom');
  targetElement.style.removeProperty('padding-left');

  // Set active section from target element before creating history point
  const sectionParent = getSectionParent(targetElement);
  if (sectionParent) {
    const sectionId = sectionParent.getAttribute('data-spmelementid');
    if (sectionId) setActiveSection(parseInt(sectionId, 10));
  }

  // Sync iframe styles to state before creating history point
  updateSectionsInState(false);
  createHistory(HistoryType.WIDGET_CHANGE);

  // Refresh handles to show new (reset) values
  const container = activeHandlesMap.get(targetElement);
  if (container) {
    container.remove();
    activeHandlesMap.delete(targetElement);
  }
  // Re-add handles with fresh padding values
  addPaddingHandles(targetElement, config);
};

/**
 * Handle mouse down on a padding handle
 */
const handleMouseDown = (
  event: MouseEvent,
  direction: PaddingDirection,
  targetElement: HTMLElement,
  handleElement: HTMLElement,
  config: PaddingHandleConfig,
): void => {
  event.preventDefault();
  event.stopPropagation();

  // Set flag to prevent widget click events
  isDraggingPaddingFlag = true;

  const currentPadding = getPaddingValue(targetElement, direction);

  dragState = {
    isDragging: true,
    direction,
    startY: event.clientY,
    startX: event.clientX,
    startPadding: currentPadding,
    targetElement,
    handleElement,
    indicator: config.showIndicator ? createIndicator() : null,
  };

  handleElement.classList.add('active');

  // Show initial indicator
  if (dragState.indicator) {
    updateIndicatorPosition(event, currentPadding);
  }

  // Cleanup any previous drag listeners (safety net)
  cleanupDragListeners();

  // Store bound references for proper removal later
  activeMoveListenerIframe = (e: Event) => handleMouseMove(e as MouseEvent, config);
  activeUpListenerIframe = (e: Event) => handleMouseUp(e as MouseEvent, config);
  activeMoveListenerDoc = (e: Event) => handleMouseMove(e as MouseEvent, config);
  activeUpListenerDoc = (e: Event) => handleMouseUp(e as MouseEvent, config);

  // Add document-level listeners
  const template = getTemplateIframeDocument();
  template.addEventListener('mousemove', activeMoveListenerIframe);
  template.addEventListener('mouseup', activeUpListenerIframe);

  // Also listen on parent document in case mouse leaves iframe
  document.addEventListener('mousemove', activeMoveListenerDoc);
  document.addEventListener('mouseup', activeUpListenerDoc);
};

/**
 * Handle mouse move during drag
 */
const handleMouseMove = (event: MouseEvent, config: PaddingHandleConfig): void => {
  if (!dragState.isDragging || !dragState.targetElement || !dragState.direction) return;

  let delta: number;

  // Calculate delta based on direction
  // Drag towards inside = increase padding (more intuitive)
  if (dragState.direction === 'top') {
    delta = event.clientY - dragState.startY; // drag down = increase top padding
  } else if (dragState.direction === 'bottom') {
    delta = dragState.startY - event.clientY; // drag up = increase bottom padding
  } else if (dragState.direction === 'left') {
    delta = event.clientX - dragState.startX; // drag right = increase left padding
  } else {
    delta = dragState.startX - event.clientX; // drag left = increase right padding
  }

  // Calculate new padding value
  const newPadding = Math.round((dragState.startPadding + delta) / config.step) * config.step;
  const clampedPadding = Math.max(config.minPadding, Math.min(config.maxPadding, newPadding));

  // If Ctrl is pressed, apply to all 4 sides
  if (event.ctrlKey) {
    const allDirections: PaddingDirection[] = ['top', 'right', 'bottom', 'left'];
    allDirections.forEach((dir) => {
      applyPaddingChange(dragState.targetElement!, dir, clampedPadding, config);
    });
    // Update all overlays and handles for visual feedback
    updateAllOverlaysAndHandles(dragState.targetElement, clampedPadding, config);
  } else {
    // Apply change to single direction
    applyPaddingChange(dragState.targetElement, dragState.direction, clampedPadding, config);
  }

  // Update indicator
  if (dragState.indicator) {
    updateIndicatorPosition(event, clampedPadding);
  }

  // Update the overlay in the container to follow the drag in real-time
  // First check widget handles
  const container = activeHandlesMap.get(dragState.targetElement);
  if (container) {
    const overlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="${dragState.direction}"]`) as HTMLElement;
    if (overlay) {
      if (dragState.direction === 'top' || dragState.direction === 'bottom') {
        overlay.style.height = `${clampedPadding}px`;
        
        // Also update left/right overlays to avoid overlap
        const leftOverlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="left"]`) as HTMLElement;
        const rightOverlay = container.querySelector(`.${PADDING_OVERLAY_CLASS}[data-direction="right"]`) as HTMLElement;
        
        if (dragState.direction === 'top') {
          if (leftOverlay) leftOverlay.style.top = `${clampedPadding}px`;
          if (rightOverlay) rightOverlay.style.top = `${clampedPadding}px`;
        } else {
          if (leftOverlay) leftOverlay.style.bottom = `${clampedPadding}px`;
          if (rightOverlay) rightOverlay.style.bottom = `${clampedPadding}px`;
        }
      } else {
        overlay.style.width = `${clampedPadding}px`;
      }
    }
    
    // Update handle position to follow the padding
    const handle = container.querySelector(`.${PADDING_HANDLE_CLASS}[data-direction="${dragState.direction}"]`) as HTMLElement;
    if (handle) {
      positionHandleInsidePadding(handle, dragState.direction, clampedPadding);
    }
  }
  
  // Row handles don't need position updates during drag - they stay fixed inside the widget
};

/**
 * Handle mouse up to end drag
 */
const handleMouseUp = (event: MouseEvent, config: PaddingHandleConfig): void => {
  if (!dragState.isDragging) return;

  try {
    // Remove active class
    if (dragState.handleElement) {
      dragState.handleElement.classList.remove('active');
    }

    // Hide indicator
    hideIndicator();
    if (dragState.indicator) {
      dragState.indicator.remove();
    }

    // Set active section from dragged element before creating history point
    if (dragState.targetElement) {
      const sectionParent = getSectionParent(dragState.targetElement);
      if (sectionParent) {
        const sectionId = sectionParent.getAttribute('data-spmelementid');
        if (sectionId) setActiveSection(parseInt(sectionId, 10));
      }
    }

    // Sync iframe styles to state before creating history point
    updateSectionsInState(false);
    createHistory(HistoryType.WIDGET_CHANGE);

    // Reset state
    dragState = {
      isDragging: false,
      direction: null,
      startY: 0,
      startX: 0,
      startPadding: 0,
      targetElement: null,
      handleElement: null,
      indicator: null,
    };

    // Remove listeners using stored references
    cleanupDragListeners();
  } finally {
    // Delay flag reset to prevent click event from triggering widget selection
    // The click event fires after mouseup, so we need a short delay
    setTimeout(() => {
      isDraggingPaddingFlag = false;
    }, 100);
  }
};

/**
 * Add padding handles to an element
 */
export const addPaddingHandles = (
  element: HTMLElement,
  config: PaddingHandleConfig = EMAIL_WIDGET_PADDING_CONFIG,
): void => {
  // Check if handles already exist
  if (activeHandlesMap.has(element)) {
    return;
  }

  // Ensure element has relative positioning
  const template = getTemplateIframeDocument();
  const computedStyle = template.defaultView?.getComputedStyle(element);
  const currentPosition = computedStyle?.position;

  if (computedStyle && currentPosition === 'static') {
    element.style.position = 'relative';
  }

  // Create handles container
  const container = template.createElement('div');
  container.className = PADDING_HANDLE_CONTAINER_CLASS;

  // Get all padding values first to avoid overlap in overlays
  const paddings = {
    top: getPaddingValue(element, 'top'),
    right: getPaddingValue(element, 'right'),
    bottom: getPaddingValue(element, 'bottom'),
    left: getPaddingValue(element, 'left'),
  };

  // Create handles and overlays for each direction
  const directions: PaddingDirection[] = ['top', 'right', 'bottom', 'left'];
  directions.forEach((direction) => {
    const paddingValue = paddings[direction];
    
    // Create overlay to show padding zone (visible on hover)
    // For left/right, pass top/bottom paddings to avoid overlap
    const overlay = createOverlay(direction, paddingValue, paddings.top, paddings.bottom);
    overlay.classList.add('spm-padding-overlay-hover');
    container.appendChild(overlay);
    
    // Create handle positioned inside the padding
    const handle = createHandle(direction);
    
    // Position handle at the inner edge of the padding
    positionHandleInsidePadding(handle, direction, paddingValue);

    // Block click propagation to prevent widget edit
    handle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingPaddingFlag = true;
      setTimeout(() => {
        isDraggingPaddingFlag = false;
      }, 150);
    }, true);

    handle.addEventListener('mousedown', (e) => {
      handleMouseDown(e as MouseEvent, direction, element, handle, config);
    });

    handle.addEventListener('dblclick', (e) => {
      handleDoubleClick(e as MouseEvent, element, config);
    });

    container.appendChild(handle);
  });

  element.appendChild(container);
  activeHandlesMap.set(element, container);
};

/**
 * Position a handle inside the padding area
 */
const positionHandleInsidePadding = (handle: HTMLElement, direction: PaddingDirection, paddingValue: number): void => {
  // Position the handle at the inner edge of the padding
  if (direction === 'top') {
    handle.style.top = `${paddingValue}px`;
    handle.style.transform = 'translateX(-50%) translateY(-50%)';
  } else if (direction === 'bottom') {
    handle.style.bottom = `${paddingValue}px`;
    handle.style.transform = 'translateX(-50%) translateY(50%)';
  } else if (direction === 'left') {
    handle.style.left = `${paddingValue}px`;
    handle.style.transform = 'translateY(-50%) translateX(-50%)';
  } else if (direction === 'right') {
    handle.style.right = `${paddingValue}px`;
    handle.style.transform = 'translateY(-50%) translateX(50%)';
  }
};

/**
 * Remove padding handles from an element
 */
export const removePaddingHandles = (element: HTMLElement): void => {
  const container = activeHandlesMap.get(element);
  if (container) {
    container.remove();
    activeHandlesMap.delete(element);
  }
};

/**
 * Remove all padding handles from the template
 */
export const removeAllPaddingHandles = (): void => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  // Clear the maps first
  activeHandlesMap.clear();
  activeRowHandlesMap.clear();

  // Then remove all containers from DOM (widget handles)
  const containers = template.querySelectorAll(`.${PADDING_HANDLE_CONTAINER_CLASS}`);
  containers.forEach((container) => container.remove());
  
  // Remove row handles containers
  const rowContainers = template.querySelectorAll(`.${PADDING_HANDLE_ROW_CONTAINER_CLASS}`);
  rowContainers.forEach((container) => container.remove());
};

/**
 * Initialize padding handles for widgets (email or embed)
 * Call this when a widget becomes active/selected
 */
export const initPaddingHandlesForWidget = (widgetElement: HTMLElement): void => {
  if (!widgetElement) {
    return;
  }

  // Inject styles if not already done
  injectPaddingHandlesStyles();

  // Check template type to determine widget structure
  const isEmail = isEmailTemplate(state.template?.type);

  if (isEmail) {
    // Email widgets: Find the th or td element inside the table widget
    let targetElement = widgetElement.querySelector(':scope > tbody > tr > th') as HTMLElement;

    if (!targetElement) {
      targetElement = widgetElement.querySelector(':scope > tbody > tr > td') as HTMLElement;
    }

    if (targetElement) {
      addPaddingHandles(targetElement, EMAIL_WIDGET_PADDING_CONFIG);
    }
  } else {
    // Display widgets (embed, popup, teaser): Use the widget element itself (DIV-based structure)
    addPaddingHandles(widgetElement, DISPLAY_WIDGET_PADDING_CONFIG);
  }
};

/**
 * Cleanup padding handles when widget is deselected
 */
export const cleanupPaddingHandlesForWidget = (widgetElement: HTMLElement): void => {
  const isEmail = isEmailTemplate(state.template?.type);

  if (isEmail) {
    // Email widgets: Remove handles from the th/td element
    const thElement = widgetElement.querySelector(':scope > tbody > tr > th') as HTMLElement;
    if (thElement) {
      removePaddingHandles(thElement);
    }
  } else {
    // Embed/Display widgets: Remove handles from the widget element itself
    removePaddingHandles(widgetElement);
  }
};

/**
 * Initialize the padding handles system
 * Should be called once when the template editor loads
 */
export const initPaddingHandlesSystem = (): void => {
  injectPaddingHandlesStyles();
};

/**
 * Find the parent row element of a widget
 */
export const getRowParentOfWidget = (widgetElement: HTMLElement): HTMLElement | null => {
  // Navigate up from the widget to find the row (table.spm_draggable_row)
  let current: HTMLElement | null = widgetElement;
  while (current) {
    if (current.classList?.contains('spm_draggable_row')) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

/**
 * Add padding handles for a row element (orange handles)
 */
export const addRowPaddingHandles = (
  element: HTMLElement,
  config: PaddingHandleConfig = EMAIL_ROW_PADDING_CONFIG,
): void => {
  // Check if handles already exist
  if (activeRowHandlesMap.has(element)) {
    return;
  }

  const template = getTemplateIframeDocument();
  const computedStyle = template.defaultView?.getComputedStyle(element);
  const currentPosition = computedStyle?.position;

  if (computedStyle && currentPosition === 'static') {
    element.style.position = 'relative';
  }

  // Create handles container with row-specific class
  const container = template.createElement('div');
  container.className = PADDING_HANDLE_ROW_CONTAINER_CLASS;

  // Get all padding values first to avoid overlap in overlays
  const paddings = {
    top: getPaddingValue(element, 'top'),
    right: getPaddingValue(element, 'right'),
    bottom: getPaddingValue(element, 'bottom'),
    left: getPaddingValue(element, 'left'),
  };

  // Create handles and overlays for each direction
  const directions: PaddingDirection[] = ['top', 'right', 'bottom', 'left'];
  directions.forEach((direction) => {
    const paddingValue = paddings[direction];
    
    // Create overlay with row-specific class
    const overlay = template.createElement('div');
    overlay.className = PADDING_OVERLAY_ROW_CLASS;
    overlay.setAttribute('data-direction', direction);
    
    if (direction === 'top' || direction === 'bottom') {
      overlay.style.height = `${paddingValue}px`;
    } else {
      overlay.style.width = `${paddingValue}px`;
      overlay.style.top = `${paddings.top}px`;
      overlay.style.bottom = `${paddings.bottom}px`;
    }
    container.appendChild(overlay);
    
    // Create handle with row-specific class
    const handle = template.createElement('div');
    handle.className = PADDING_HANDLE_ROW_CLASS;
    handle.setAttribute('data-direction', direction);
    handle.setAttribute('title', `Ajuster le padding ligne ${direction}`);
    
    // Position handle inside the padding
    if (direction === 'top') {
      handle.style.top = `${paddingValue}px`;
      handle.style.transform = 'translateX(-50%) translateY(-50%)';
    } else if (direction === 'bottom') {
      handle.style.bottom = `${paddingValue}px`;
      handle.style.transform = 'translateX(-50%) translateY(50%)';
    } else if (direction === 'left') {
      handle.style.left = `${paddingValue}px`;
      handle.style.transform = 'translateY(-50%) translateX(-50%)';
    } else if (direction === 'right') {
      handle.style.right = `${paddingValue}px`;
      handle.style.transform = 'translateY(-50%) translateX(50%)';
    }

    handle.addEventListener('mousedown', (e) => {
      handleMouseDown(e as MouseEvent, direction, element, handle, config);
    });

    container.appendChild(handle);
  });

  element.appendChild(container);
  activeRowHandlesMap.set(element, container);
};

/**
 * Initialize padding handles for a row (line) element
 * @param rowElement - The row table element (table.spm_draggable_row)
 * @param widgetElement - The widget element to attach handles to (for accessibility)
 */
export const initPaddingHandlesForRow = (rowElement: HTMLElement, widgetElement?: HTMLElement): void => {
  if (!rowElement) return;
  
  // Inject styles if not already done
  injectPaddingHandlesStyles();

  // Find the td element inside the row (this is the element whose padding we control)
  const rowTdElement = rowElement.querySelector(':scope > tbody > tr > td') as HTMLElement;
  
  if (rowTdElement) {
    // If a widget element is provided, attach handles to widget for better accessibility
    // but control the row's TD padding
    if (widgetElement) {
      addRowPaddingHandlesToWidget(rowTdElement, widgetElement, EMAIL_ROW_PADDING_CONFIG);
    } else {
      addRowPaddingHandles(rowTdElement, EMAIL_ROW_PADDING_CONFIG);
    }
  }
};

/**
 * Add row padding handles attached to widget element for better accessibility
 * The handles control the row's TD padding but are positioned relative to the widget
 */
const addRowPaddingHandlesToWidget = (
  rowTdElement: HTMLElement,
  widgetElement: HTMLElement,
  config: PaddingHandleConfig,
): void => {
  // Check if handles already exist for this row TD
  if (activeRowHandlesMap.has(rowTdElement)) {
    return;
  }

  const template = getTemplateIframeDocument();
  
  // Ensure widget has relative positioning
  const computedStyle = template.defaultView?.getComputedStyle(widgetElement);
  if (computedStyle && computedStyle.position === 'static') {
    widgetElement.style.position = 'relative';
  }

  // Create handles container attached to widget
  const container = template.createElement('div');
  container.className = PADDING_HANDLE_ROW_CONTAINER_CLASS;

  // Get padding values from the row TD
  const paddings = {
    top: getPaddingValue(rowTdElement, 'top'),
    right: getPaddingValue(rowTdElement, 'right'),
    bottom: getPaddingValue(rowTdElement, 'bottom'),
    left: getPaddingValue(rowTdElement, 'left'),
  };

  // Create handles for all 4 directions - positioned INSIDE the widget with offset from widget handles
  const directions: PaddingDirection[] = ['top', 'right', 'bottom', 'left'];
  const HANDLE_OFFSET = 20; // Offset from widget handles in pixels
  
  directions.forEach((direction) => {
    const paddingValue = paddings[direction];
    
    // Create handle - positioned inside the widget with offset
    const handle = template.createElement('div');
    handle.className = PADDING_HANDLE_ROW_CLASS;
    handle.setAttribute('data-direction', direction);
    handle.setAttribute('title', `Padding ligne ${direction}`);
    
    // Position handles inside the widget, offset from the widget's own handles
    if (direction === 'top') {
      handle.style.top = `${HANDLE_OFFSET}px`;
      handle.style.left = '50%';
      handle.style.transform = 'translateX(-50%)';
    } else if (direction === 'bottom') {
      handle.style.bottom = `${HANDLE_OFFSET}px`;
      handle.style.left = '50%';
      handle.style.transform = 'translateX(-50%)';
    } else if (direction === 'left') {
      handle.style.left = `${HANDLE_OFFSET}px`;
      handle.style.top = '50%';
      handle.style.transform = 'translateY(-50%)';
    } else {
      handle.style.right = `${HANDLE_OFFSET}px`;
      handle.style.top = '50%';
      handle.style.transform = 'translateY(-50%)';
    }

    handle.addEventListener('mousedown', (e) => {
      // Pass rowTdElement as the target to modify its padding
      handleMouseDown(e as MouseEvent, direction, rowTdElement, handle, config);
    });

    container.appendChild(handle);
  });

  // Attach to widget element
  widgetElement.appendChild(container);
  activeRowHandlesMap.set(rowTdElement, container);
};
